/**
 * Two-mode master picker — full panel inline OR compact dropdown.
 *
 * The "Any master" pseudo-record is rendered first; selecting it lets the
 * time-slot picker pull from the union of all eligible masters and assign
 * a concrete person at the moment the user picks a slot. This is the same
 * default alteg.io ships, and it always converts better than forcing the
 * user to recognize a stylist they may not know yet.
 */

import { useTranslation } from "react-i18next";
import { ANY_MASTER } from "./SalonBookingContext";
import { pickLocalized } from "./salonTheme";

function StarRating({ value, palette }) {
  if (!value) return null;
  return (
    <span
      className="d-inline-flex align-items-center gap-1"
      style={{ color: palette.textMuted, fontSize: "0.82rem" }}
    >
      <i
        className="las la-star"
        style={{ color: "hsl(45 95% 55%)", fontSize: "0.95rem" }}
        aria-hidden="true"
      />
      <span className="fw-600" style={{ color: palette.text }}>
        {value.toFixed(2)}
      </span>
    </span>
  );
}

function getReviewCount(master) {
  const candidates = [
    master?.reviewsCount,
    master?.reviews_count,
    master?.ratingCount,
    master?.rating_count,
    master?.ratingsCount,
    master?.ratings_count,
  ];

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value > 0) return value;
  }

  return 0;
}

function MasterCard({ palette, master, selected, onClick, lang, label, sublabel }) {
  const { t } = useTranslation();
  const reviewCount = getReviewCount(master);

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-start w-100"
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        padding: "12px 14px",
        borderRadius: 14,
        background: selected ? palette.accentSoft : palette.surface,
        border: selected
          ? `1.5px solid ${palette.accentBorder}`
          : palette.border,
        cursor: "pointer",
        color: palette.text,
        transition: "background 0.15s ease, border-color 0.15s ease",
        textAlign: "left",
      }}
    >
      {master?.photo ? (
        <img
          src={master.photo}
          alt=""
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
            boxShadow: palette.shadowSoft,
          }}
        />
      ) : (
        <span
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: palette.accentSoft,
            color: palette.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.6rem",
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <i className="las la-users" />
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          className="d-block fw-700"
          style={{
            color: palette.text,
            fontSize: "1rem",
            lineHeight: 1.2,
            wordBreak: "break-word",
          }}
        >
          {label}
        </span>
        <span
          className="d-block"
          style={{
            color: palette.textMuted,
            fontSize: "0.85rem",
            marginTop: 2,
            wordBreak: "break-word",
          }}
        >
          {sublabel}
        </span>
        {master?.rating ? (
          <span className="d-inline-flex mt-1">
            <StarRating value={master.rating} palette={palette} />
            {reviewCount ? (
              <span
                style={{
                  fontSize: "0.78rem",
                  color: palette.textSubtle,
                  marginLeft: 6,
                }}
              >
                ({t("booking.specialists.reviewsCount", { count: reviewCount })})
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
      {selected ? (
        <span
          aria-hidden="true"
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: palette.accent,
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.9rem",
            flexShrink: 0,
          }}
        >
          <i className="las la-check" />
        </span>
      ) : null}
    </button>
  );
}

/**
 * `masters`: eligible masters (filtered by the parent based on the cart).
 *
 * `selectedId` is the master id currently chosen in context — either
 * `ANY_MASTER` or a concrete master id.
 */
export function MasterPicker({ palette, lang, masters, selectedId, onSelect }) {
  const { t } = useTranslation();

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <MasterCard
        palette={palette}
        master={null}
        selected={selectedId === ANY_MASTER}
        onClick={() => onSelect(ANY_MASTER)}
        lang={lang}
        label={t("salonBooking.master.anyTitle")}
        sublabel={t("salonBooking.master.anySubtitle")}
      />
      {masters.length === 0 ? (
        <div
          style={{
            background: palette.surface,
            border: palette.border,
            borderRadius: 14,
            padding: "16px 14px",
            textAlign: "center",
            color: palette.textMuted,
            fontSize: "0.9rem",
          }}
        >
          {t("salonBooking.master.noEligible")}
        </div>
      ) : (
        masters.map((master) => (
          <MasterCard
            key={master.id}
            palette={palette}
            master={master}
            selected={selectedId === master.id}
            onClick={() => onSelect(master.id)}
            lang={lang}
            label={pickLocalized(master.name, lang)}
            sublabel={
              pickLocalized(master.title, lang) +
              (master.yearsExperience
                ? ` • ${t("salonBooking.master.yearsExperience", {
                    count: master.yearsExperience,
                  })}`
                : "")
            }
          />
        ))
      )}
    </div>
  );
}
